import { CertificateResponse } from '../PWSigner/types'

interface LoginResponse {
  success: boolean
  message: string
  [key: string]: any
}

interface AuthConfig {
  loginURL: string
  finalizeURL: string
  initializeURL: string
  checkUserURL: string
}

interface InitResponseType {
  transactionUUID: string
  success: boolean
  digestBase64: string
}

class AuthenticationError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthenticationError'
  }
}

export class UYAPAuthenticator {
  constructor(
    private readonly config: AuthConfig,
    private readonly setLocalStorage: (key: string, value: string) => void,
    private readonly setSessionStorage: (key: string, value: string) => void,
  ) {}

  /**
   * Login işlemini gerçekleştirir
   * @param pinCode - Kullanıcı PIN kodu
   * @param terminal - Terminal bilgileri
   * @param certificate - Sertifika bilgileri
   * @returns Login işlemi sonucu
   */
  async login({
    certificate,
    signature,
  }: {
    certificate: CertificateResponse
    signature: string
  }): Promise<LoginResponse> {
    try {
      // 1. İmzalama işlemini başlat
      const initResponse = await this.initializeLogin(certificate.certificate)

      if (!initResponse.success) {
        throw new AuthenticationError('İmzalama işlemi sırasında hata oluştu!')
      }

      if (!signature) {
        throw new AuthenticationError(
          'İmzalama sırasında bir sorun ile karşılaşılmıştır.',
        )
      }

      // 3. İmzalamayı tamamla
      const finalizeResponse = await this.finalizeLogin({
        signatureBase64: signature,
        transactionUUID: initResponse.transactionUUID,
        certificateCommonName: certificate.commonName,
      })

      if (!finalizeResponse.success) {
        throw new AuthenticationError(
          'İmzalama sırasında bir sorun ile karşılaşılmıştır.',
        )
      }

      // 4. Login işlemini tamamla
      try {
        await this.loginSetCookieRequest()
      } catch (e) {
        console.error(
          'Giriş sırasında hata oluştu, ancak işleme devam edilebilir',
          e,
        )
      }

      // 5. Oturum bilgilerini kaydet
      this.setLocalStorage('loginState', 'true')
      this.setSessionStorage('showKvkPopup', 'true')
      this.setSessionStorage('showPopupDuyuru2', 'true')

      return { success: true, message: 'Giriş başarılı' }
    } catch (error: any) {
      if (error instanceof AuthenticationError) {
        throw error
      }
      throw new AuthenticationError(
        `Login işlemi sırasında hata: ${error.message}`,
      )
    }
  }

  initializeLogin = (
    certificateBase64: string,
    requestInit?: RequestInit,
    responseType?: 'json' | 'text' | 'blob' | 'response',
  ): Promise<InitResponseType> =>
    this.sendRequest<InitResponseType>({
      url: this.config.initializeURL,
      method: 'GET',
      data: {
        action: 'initialize-signing',
        signatureType: 1,
        certificateBase64,
      },
      requestInit,
      responseType,
    })

  finalizeLogin = (
    data: {
      signatureBase64: string
      transactionUUID: string
      certificateCommonName: string
    },
    requestInit?: RequestInit,
  ): Promise<LoginResponse> =>
    this.sendRequest<LoginResponse>({
      url: this.config.finalizeURL,
      method: 'GET',
      data: { action: 'finalize-signing', ...data },
      requestInit,
    })

  loginSetCookieRequest = (requestInit?: RequestInit) =>
    this.sendRequest<void>({
      url: this.config.loginURL,
      method: 'POST',
      responseType: 'text',
      requestInit,
    })

  /**
   * Mevcut kullanıcı oturumunu kontrol eder
   * @returns Oturum durumu (true: aktif, false: pasif)
   */
  async checkSession(): Promise<boolean> {
    try {
      const response = await this.sendRequest<{ error: boolean }>({
        url: this.config.checkUserURL,
        method: 'GET',
      })

      return response && !response.error
    } catch (error) {
      console.error('Oturum kontrolü sırasında hata:', error)
      return false
    }
  }

  private async sendRequest<T>({
    url,
    method,
    data = {},
    responseType = 'json',
    requestInit = {},
  }: {
    url: string
    method: 'GET' | 'POST'
    data?: Record<string, any>
    responseType?: 'json' | 'text' | 'blob' | 'response'
    requestInit?: RequestInit
  }): Promise<T> {
    try {
      let requestUrl = url
      let requestOptions: RequestInit = {
        method,
        headers: { 'Content-Type': 'application/json' },
        ...requestInit,
      }

      if (method === 'GET' && Object.keys(data).length > 0) {
        const params = new URLSearchParams()
        for (const key in data) {
          params.append(key, data[key])
        }
        requestUrl = `${url}?${params.toString()}`
      } else if (method === 'POST' && Object.keys(data).length > 0) {
        requestOptions.body = JSON.stringify(data)
      }

      const response = await fetch(requestUrl, requestOptions)

      if (!response.ok) {
        throw new Error(`HTTP hatası! Durum: ${response.status}`)
      }

      if (responseType === 'response') return response as any

      return response[responseType]()
    } catch (error: any) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Ağ hatası oluştu!')
      }
      if (error.message.includes('JSON')) {
        throw new Error('JSON ayrıştırma hatası!')
      }
      throw error
    }
  }
}
